import React, { useState } from 'react'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  Text
} from '@chakra-ui/react'
import {
  FaDownload,
  FaFile,
  FaFileExcel,
  FaFilePdf,
  FaFolder,
  FaPlus
} from 'react-icons/fa'
import { format } from 'date-fns'
import StatusLogTimeline from './StatusLogTimeline'
import { exportDocumentZIPRequest } from '../../api/exportDocumentZIPRequest'
import { exportXmlRequest } from '../../api/exportDocumentXml'
import { exportPdfRequest } from '../../api/exportDocumentPdf'
import getPrettyStatus from '../../../../helpers/status'
import SupportingDocumentsModal from '../SupportingDocumentsModal'
import { DOCUMENT_STATUS, LABEL } from '../../../../constants'
import ExcelUploadModal from './ExcelUploadModal'
import { exportProcessToExcel } from '../../api/exportProcessToExcel'

const DocumentMetaData = ({
  document,
  documentData,
  setFieldValue,
  costCenters,
  expenseGroups,
  projects,
  user,
  cif
}) => {
  const [isModalOpen, setModalOpen] = useState(false)
  const [isExcelModalOpen, setExcelModalOpen] = useState(false)

  const handleExcelDataParsed = (parsedData) => {
    if (setFieldValue) {
      setFieldValue('documentLines', parsedData)
    } else {
      console.warn('setFieldValue nu e valabil')
    }
  }

  return (
    <Box mb="5">
      <Accordion allowToggle>
        <AccordionItem>
          <Flex
            p="3"
            align="center"
            gap="5"
            justifyContent="space-between"
            borderRadius="md"
          >
            <Box display="flex" alignItems="center" gap="3">
              {/* Status Pill (Centered Vertically) */}
              <Badge
                colorScheme={
                  document.status === DOCUMENT_STATUS.PENDING.key
                    ? 'gray'
                    : document.status === DOCUMENT_STATUS.PROCESSED.key
                    ? 'blue'
                    : document.status === DOCUMENT_STATUS.APPROVED.key
                    ? 'orange'
                    : document.status === DOCUMENT_STATUS.SENT_TO_OMC.key
                    ? 'green'
                    : 'gray'
                }
                px="3"
                py="1"
                borderRadius="full"
                fontSize="sm"
              >
                {getPrettyStatus(document.status)}
              </Badge>

              {/* Gray Divider */}
              <Box height="40px" width="1px" bg="gray.300" />

              {/* Index and Date Stacked */}
              <Flex flexDirection="column">
                <Text fontWeight="bold" color="blue.500">
                  Index incarcare:
                  <Text
                    as="span"
                    fontWeight="normal"
                    fontSize="sm"
                    color="black"
                  >
                    {document.downloadId}
                  </Text>
                </Text>
                <Text fontWeight="bold" color="blue.500">
                  Data incarcare:
                  <Text
                    as="span"
                    fontWeight="normal"
                    fontSize="sm"
                    color="black"
                  >
                    {format(new Date(document.spvTimestamp), 'yyyy-MM-dd')}
                  </Text>
                </Text>
              </Flex>
            </Box>

            <Flex align="center" gap="5">
              <>
                <Button
                  size="sm"
                  colorScheme="purple"
                  variant="outline"
                  leftIcon={<Icon as={FaPlus} />}
                  _hover={{ bg: 'purple.500', color: 'white' }}
                  onClick={() => setModalOpen(true)}
                >
                  {LABEL.PROCESS_BUTTONS.supportingDocuments}
                </Button>

                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  _hover={{ bg: 'blue.500', color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    exportProcessToExcel(document.id)
                  }}
                  leftIcon={<Icon as={FaDownload} />}
                >
                  {LABEL.DOCUMENT_METADATA.EXPORT_EXCEL}
                </Button>

                {!user.user.isGuest && (
                  <Button
                    size="sm"
                    colorScheme="brand"
                    variant="outline"
                    leftIcon={<Icon as={FaFileExcel} />}
                    _hover={{ bg: 'brand.500', color: 'white' }}
                    onClick={() => setExcelModalOpen(true)}
                  >
                    {LABEL.DOCUMENT_METADATA.IMPORT_EXCEL}
                  </Button>
                )}

                <Button
                  size="sm"
                  colorScheme="orange"
                  variant="outline"
                  _hover={{ bg: 'orange.500', color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    exportDocumentZIPRequest(document.id)
                  }}
                  leftIcon={<Icon as={FaFolder} />}
                >
                  {LABEL.DOCUMENT_METADATA.DOWNLOAD_ZIP}
                </Button>

                <Button
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  _hover={{ bg: 'green.500', color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    exportXmlRequest(document.id)
                  }}
                  leftIcon={<Icon as={FaFile} />}
                >
                  {LABEL.DOCUMENT_METADATA.DOWNLOAD_XML}
                </Button>

                <Button
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  _hover={{ bg: 'blue.500', color: 'white' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    exportPdfRequest(document.id)
                  }}
                  leftIcon={<Icon as={FaFilePdf} />}
                >
                  {LABEL.DOCUMENT_METADATA.DOWNLOAD_PDF}
                </Button>

                <AccordionButton
                  as={Button}
                  width="300px"
                  variant="outline"
                  colorScheme="gray"
                  _hover={{ bg: 'gray.600', color: 'white' }}
                  rightIcon={<AccordionIcon />}
                >
                  {LABEL.DOCUMENT_METADATA.STATUS_HISTORY}
                </AccordionButton>
              </>
            </Flex>
          </Flex>
          <AccordionPanel pb={2} bg="gray.50">
            <StatusLogTimeline statusLog={document.statusLog} />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      <SupportingDocumentsModal
        documentId={document.id}
        isOpen={isModalOpen}
        user={user}
        onClose={() => setModalOpen(false)}
      />

      <ExcelUploadModal
        isOpen={isExcelModalOpen}
        onClose={() => setExcelModalOpen(false)}
        onDataParsed={handleExcelDataParsed}
        expenseGroup={expenseGroups}
        costCenters={costCenters}
        projects={projects}
        cif={cif}
        documentLines={documentData?.documentLines}
      />
    </Box>
  )
}

export default DocumentMetaData
